import React, { createContext, useEffect, useRef, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { AudioOutlined } from '@ant-design/icons';
import {
	message,
	Popover,
	Layout,
	Card,
	Space,
	Typography,
	Button,
	Input,
	Tooltip,
	Switch,
} from 'antd';
import {
	SendOutlined,
	LoadingOutlined,
	QuestionCircleOutlined,
	SubnodeOutlined,
	CloseOutlined,
} from '@ant-design/icons';
import AudioRecorder from 'audio-recorder-polyfill';
import { mergeStateObject } from '../../utils/merger';
import { fetchWrapper } from '../../helpers';
import { getLastArrayObjects } from '../../helpers/getLastArrayObjects';
import paperClipIcon from '../../pages/icons/paperclip.svg';
import { useOrganization } from '../../context/organizationContext';
import ChatMessage from './ChatMessage';
import { extractSystemMessage } from '../../helpers/extract-system-message';
import { GetFormattedChatMessageForKeyType } from '../../utils/get-formatted-chat-message-for-key-type';
import { useConfirm } from '../../hooks/useConfirm';

export const ChatContext = createContext(undefined);

const AssistantChat = () => {
	const org = useOrganization();
	const confirm = useConfirm();
	const maxLengthMessage = 2000;
	try {
		if (typeof window !== 'undefined' && !window.MediaRecorder) {
			window.MediaRecorder = AudioRecorder;
		}
	} catch {}
	const textAreaRef = useRef(null);
	const fileInputRef = useRef(null);
	const quickFileInputRef = useRef(null);
	const chatScrollContainer = useRef(null);
	const mediaRecorderRef = useRef(null);
	const mediaStreamRef = useRef(null);
	const { t } = useTranslation();
	const [messages, setMessages] = useState([]);
	const [myMessage, setMyMessage] = useState('');
	const [quickFile, setQuickFile] = useState(null);
	const [isInternetSearching, setIsInternetSearching] = useState(() => {
		try {
			return sessionStorage.getItem('isInternetSearching') || false;
		} catch {
			return false;
		}
	});
	const [makeQuestions, setMakeQuestions] = useState(false);
	const [fileData, setFileData] = useState({
		fileInfo: undefined,
		file: false,
		useFile: false,
		newFile: null,
	});
	const [socialNetworks, setSocialNetworks] = useState('');
	const [loadings, setLoadings] = useState({
		sendMessage: false,
		recording: false,
		uploadAudio: false,
	});
	const apiBase = process.env.REACT_APP_API_URL;
	const apiUnavailable = !apiBase;

	const contentUser = (
		<pre style={{ fontSize: 12 }} className="text-[10px] block max-w-full font-inherit">
			{' '}
			{t('assistant.assistantFilesInstructionContent')}{' '}
		</pre>
	);
	const contentEmployee = (
		<pre style={{ fontSize: 12 }} className="text-[10px] block max-w-full font-inherit">
			{' '}
			{t('assistant.assistantFilesInstructionEmployeeContent')}{' '}
		</pre>
	);
	const contentISearch = (
		<>
			<pre style={{ fontSize: 12 }} className="text-[10px] block max-w-full font-inherit">
				{t('assistant.assistantInternetSearchInstructionContent')}
			</pre>
			<p
				className="text-xs mt-2"
				dangerouslySetInnerHTML={{ __html: t('support.ifQuestionGetSupport') }}
			/>
		</>
	);
	const contentMakeQuestions = (
		<pre style={{ fontSize: 12 }} className="text-[10px] block max-w-full font-inherit">
			{t('assistant.assistantMakeQuestionsPopoverContent')}
		</pre>
	);
	const contentQuickFile = quickFile ? (
		<div className="w-full flex justify-between items-center">
			<p className="text-nowrap overflow-hidden text-ellipsis">{quickFile?.name}</p>
			<button onClick={() => setQuickFile(null)} className="ml-2">
				<CloseOutlined className="text-red-600 opacity-75 hover:opacity-100" />
			</button>
		</div>
	) : (
		<div className="text-xs">{t('assistant.assistantQuickFilePopoverInfo')}</div>
	);

	const sendMessage = async ({
		my_msg = myMessage,
		returnMyMessage = false,
		forceISearching = undefined,
	}) => {
		if (apiUnavailable) {
			message.error('API URL не задан.');
			return;
		}
		if (!my_msg.trim()) return;
		const lastMessage = myMessage;
		const nowDate = new Date();
		message.loading(t('functions.sending'));
		mergeStateObject(setLoadings, { sendMessage: true });
		setMessages((prev) => [
			...prev,
			{
				role: 'user',
				content: GetFormattedChatMessageForKeyType(t, my_msg),
				date: nowDate,
				quickFile: quickFile?.name || null,
			},
		]);
		setMyMessage('');
		const formData = new FormData();
		formData.append('content', my_msg);
		formData.append('quickFile', quickFile);
		formData.append('memo', JSON.stringify(getLastArrayObjects(messages, 10)));
		formData.append('useFile', fileData.useFile);
		formData.append('search', forceISearching ?? isInternetSearching);
		formData.append('makeQuestions', makeQuestions);
		try {
			const data = await fetchWrapper.post(`${apiBase}/assistant/chat`, formData);
			setMessages((prev) => [
				...prev,
				{
					role: data?.role,
					content: data?.content,
					date: data?.date || new Date(),
					quickFile: data?.quickFile || null,
				},
			]);
			setQuickFile(null);
			mergeStateObject(setFileData, { useFile: false });
			if (returnMyMessage) setMyMessage(lastMessage);
		} catch (err) {
			setMyMessage(lastMessage);
			setMessages((prev) => prev.filter((m) => m.date !== nowDate));
			message.error(t('assistant.sendChatMessageErrorText'));
		} finally {
			mergeStateObject(setLoadings, { sendMessage: false });
		}
	};

	const prevModerateMessage = () => {
		if (
			(myMessage.toLowerCase().includes('пост') || myMessage.toLowerCase().includes('post')) &&
			isInternetSearching
		) {
			// Можно добавить предупреждение позже
			sendMessage({});
		} else sendMessage({});
	};

	const startRecording = async () => {
		try {
			const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
			mediaStreamRef.current = stream;

			mergeStateObject(setLoadings, { recording: true, uploadAudio: false });

			// pick a supported mime (Chrome -> webm/opus; Firefox may use ogg/opus)
			const prefer = ['audio/webm;codecs=opus', 'audio/webm', 'audio/ogg;codecs=opus', 'audio/ogg'];
			const mimeType = prefer.find((m) => MediaRecorder.isTypeSupported(m)) || '';

			const rec = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
			mediaRecorderRef.current = rec;

			const chunks = [];
			rec.ondataavailable = (e) => {
				if (e.data && e.data.size) chunks.push(e.data);
			};
			rec.onstop = async () => {
				try {
					const type = mimeType || chunks[0]?.type || 'audio/webm';
					const ext = type.includes('ogg') ? 'ogg' : 'webm';
					const blob = new Blob(chunks, { type });
					const file = new File([blob], `recording.${ext}`, { type });

					const formData = new FormData();
					formData.append('audio', file);

					mergeStateObject(setLoadings, { uploadAudio: true });
					const data = await fetchWrapper.post(`${apiBase}/upload-audio`, formData);

					// server should return JSON: { text: "..." }
					const text = typeof data === 'string' ? data : data?.text ?? '';
					setMyMessage((prev) => (prev ? `${prev}\n${text}` : text));
				} finally {
					mergeStateObject(setLoadings, { uploadAudio: false });
					// release mic
					if (mediaStreamRef.current) {
						mediaStreamRef.current.getTracks().forEach((t) => t.stop());
						mediaStreamRef.current = null;
					}
				}
			};

			rec.start(); // you can pass timeslice if you want interim chunks
		} catch (err) {
			message.error(t('functions.microphone'));
		}
	};

	const stopRecording = () => {
		mergeStateObject(setLoadings, { recording: false });
		if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
			mediaRecorderRef.current.stop();
		} else if (mediaStreamRef.current) {
			mediaStreamRef.current.getTracks().forEach((t) => t.stop());
			mediaStreamRef.current = null;
		}
	};
	const toggleMic = () => {
		if (loadings.recording) {
			stopRecording();
			mergeStateObject(setLoadings, { uploadAudio: true });
			textAreaRef.current?.focus();
		} else startRecording();
	};
	const handleAddQuickFile = (e) => {
		e.preventDefault();
		if (e.target.files[0]) setQuickFile(e.target.files[0]);
		e.target.value = null;
	};
	const handleFileChange = (e) => {
		const file = e.target.files[0];
		if (!file) return;
		mergeStateObject(setFileData, { newFile: file });
	};

	const handleSwitchInternetSearching = async (checked) => {
		const ok = await confirm({
			title: t('assistant.useInternetSearchingConfirmTitle') || 'Подтверждение',
			message:
				t('assistant.useInternetSearchingConfirm') ||
				'Вы уверены, что хотите включить поиск в интернете?',
			confirmText: t('common.yes') || 'Да',
			cancelText: t('common.no') || 'Нет',
		});

		if (!ok) return;

		if (checked) {
			setIsInternetSearching(true);
			sessionStorage.setItem('isInternetSearching', true);
		} else {
			setIsInternetSearching(false);
			sessionStorage.removeItem('isInternetSearching');
		}
	};

	useEffect(() => {
		if (apiUnavailable) return;
		fetchWrapper
			.get(`${apiBase}/assistant/history`)
			.then((data) => {
				if (Array.isArray(data)) {
					const filtered = data.map((msg) =>
						typeof msg.content === 'string' && extractSystemMessage(msg.content)
							? { ...msg, content: GetFormattedChatMessageForKeyType(t, msg.content) }
							: msg,
					);
					setMessages(filtered);
				}
			})
			.catch(() => message.error(t('functions.wrong')));
		fetchWrapper
			.get(`${apiBase}/assistant/files`)
			.then((data) => {
				if (data.file) mergeStateObject(setFileData, { file: true, fileInfo: data?.userFile });
			})
			.catch(() => {});
	}, [apiUnavailable]);

	useEffect(
		() => () => {
			if (mediaRecorderRef.current?.state === 'recording') mediaRecorderRef.current.stop();
		},
		[],
	);
	useEffect(() => {
		if (chatScrollContainer.current)
			chatScrollContainer.current.scrollTop = chatScrollContainer.current.scrollHeight + 500;
	}, [messages]);
	useEffect(() => {
		textAreaRef.current?.focus();
	}, [loadings]);

	const contextValue = { myMessage, setMyMessage, sendMessage };
	const helloDate = useMemo(() => new Date(), []);
	return (
		<ChatContext.Provider value={contextValue}>
			{/* Mobile-first CSS (no logic changes) */}
			<style>{`
        :root {
          --page-max: 1200px;
          --pad: 16px;
        }
        @media (max-width: 575.98px) {
          :root { --pad: 10px; }
          .asst-title { font-size: clamp(16px, 4.5vw, 18px) !important; margin: 0 !important; }
          .asst-header-actions .ant-space-split { display: none; } /* hide vertical dividers on xs */
          .asst-content-pad { padding: var(--pad) var(--pad) 0 var(--pad) !important; gap: 8px !important; }
          .asst-card-inner { border-radius: 10px !important; }
          .asst-input-row { row-gap: 8px !important; }
          .asst-send-btn { width: 100%; }
          .asst-mic-btn { width: 100%; }
          .asst-footer { padding-bottom: calc(12px + env(safe-area-inset-bottom)) !important; }
          .asst-hello { max-width: 100% !important; }
        }
        /* Make the input bar sticky within the card area */
        .asst-footer {
          position: sticky;
          bottom: 0;
          background: rgba(255,255,255,0.88);
          backdrop-filter: blur(6px);
        }
        /* Improve scroll area snapping on mobile */
        .asst-scroll {
          scroll-behavior: smooth;
          -webkit-overflow-scrolling: touch;
        }
      `}</style>

			<Layout
				style={{
					height: '85dvh',
					minHeight: '80dvh',
					display: 'flex',
					flexDirection: 'column',
					background: 'linear-gradient(180deg,#ffffff,#faf5ff)',
				}}
			>
				{apiUnavailable && (
					<div
						style={{
							background: '#fff1f0',
							border: '1px solid #ffa39e',
							padding: 12,
							margin: 16,
							borderRadius: 6,
							color: '#a8071a',
						}}
					>
						REACT_APP_API_URL не задан.
					</div>
				)}

				<Layout.Content
					style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
				>
					<div
						className="asst-content-pad"
						style={{
							flex: 1,
							maxWidth: 'var(--page-max)',
							width: '100%',
							margin: '0 auto',
							padding: '16px 16px 0',
							display: 'flex',
							gap: 16,
							overflow: 'hidden',
						}}
					>
						{/* Chat column */}
						<div
							style={{
								flex: 1,
								minWidth: 0,
								display: 'flex',
								flexDirection: 'column',
								overflow: 'hidden',
							}}
						>
							<Card
								className="shadow-sm asst-card-inner"
								bordered={true}
								bodyStyle={{
									padding: 0,
									height: '100%',
									display: 'flex',
									flexDirection: 'column',
									minHeight: 0,
								}}
								style={{ flex: 1, display: 'flex', minHeight: 0 }}
							>
								<div
									ref={chatScrollContainer}
									className="asst-scroll"
									style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: 12 }}
								>
									{/* Приветствие как обычное сообщение ассистента */}
									<ChatMessage
										message={{
											role: 'assistant',
											content: t('assistant.helloMessage'),
											date: helloDate,
										}}
										snActions={{ setSocialNetworks, socialNetworks }}
									/>

									{Array.isArray(messages) &&
										messages.length > 0 &&
										messages.map((m, i) => (
											<ChatMessage
												key={i}
												message={m}
												snActions={{ setSocialNetworks, socialNetworks }}
											/>
										))}

									{loadings.sendMessage && (
										<Card
											size="small"
											bordered={true}
											style={{ maxWidth: '90%', opacity: 0.6 }}
											bodyStyle={{ padding: 12 }}
										>
											<Space>
												<LoadingOutlined />
												<Typography.Text>{t('assistant.pleaseWaitForResponse')}</Typography.Text>
											</Space>
										</Card>
									)}
								</div>

								{/* Sticky composer */}
								<div
									className="asst-footer"
									style={{ borderTop: '1px solid #f0f0f0', padding: 12 }}
								>
									<style>{`
    /* Structure */
    .asst-composer { display:flex; flex-direction:column; gap:8px; }
    .asst-composer-row { display:flex; flex-direction:column; gap:8px; }

    /* Input */
    .asst-textarea-wrap {
      border: 1px solid #e5e7eb;
      border-radius: 12px;
      padding: 6px;
      background: #fff;
    }
    .asst-textarea-wrap .ant-input {
      border: none !important;
      box-shadow: none !important;
      resize: none;
      padding: 8px 10px !important;
    }

    /* Tools line (below input) */
    .asst-tools-row {
      display: grid;
      grid-template-columns: 1fr auto; /* left tools | right actions (md+) */
      align-items: center;
      gap: 8px;
    }
    .asst-tools-left {
      display: flex; gap: 8px; align-items: center;
    }

    /* Right actions */
    .asst-actions-right { display:flex; gap:8px; align-items:center; }
    .asst-icon-btn { border-radius: 10px; }
    .asst-icon-btn:hover { background: rgba(0,0,0,.04); }
    .asst-send-btn { white-space: nowrap; }

    /* Meta row */
    .asst-meta-row { display:flex; align-items:center; justify-content:space-between; gap:8px; }
    .asst-counter { font-size:12px; color:#8c8c8c; }

    /* Mobile */
    @media (max-width: 575.98px) {
      .asst-tools-row { grid-template-columns: 1fr; gap: 6px; }
      .asst-tools-left {
        overflow-x: auto; white-space: nowrap; padding-bottom: 2px;
        -ms-overflow-style: none; scrollbar-width: none;
      }
      .asst-tools-left::-webkit-scrollbar { display: none; } /* hide scrollbar */
      .asst-actions-right { justify-content: flex-end; }
      .asst-send-btn { width: 100%; } /* big, tappable */
      .asst-header-actions .ant-space-split { display: none; } /* no vertical dividers on xs */
    }

    /* Sticky feel + safe area */
    .asst-footer {
      position: sticky; bottom: 0;
      background: rgba(255,255,255,0.88); backdrop-filter: blur(6px);
      padding-bottom: calc(12px + env(safe-area-inset-bottom));
    }
  `}</style>

									<div className="asst-composer">
										<div className="asst-composer-row">
											{/* 1) Full-width INPUT on all screens */}
											<div className="asst-textarea-wrap">
												<Input.TextArea
													ref={textAreaRef}
													autoSize={{ minRows: 2, maxRows: 8 }}
													placeholder={`${t('assistant.yourMessage')}…`}
													value={myMessage}
													disabled={
														loadings.recording || loadings.uploadAudio || loadings.sendMessage
													}
													onChange={(e) => {
														if (e.target.value.length > maxLengthMessage + 50000) return;
														setMyMessage(e.target.value);
													}}
													onKeyDown={(e) => {
														e.ctrlKey &&
															e.code === 'KeyV' &&
															message.success(t('functions.pastedFromClipboard'));
														e.key === 'Enter' && e.ctrlKey && prevModerateMessage();
													}}
													allowClear
												/>
											</div>

											{/* 2) Tools BELOW the input */}
											<div className="asst-tools-row">
												{/* Left tool strip (scrollable on mobile) */}
												<div className="asst-tools-left asst-header-actions">
													<Popover
														overlayStyle={{ maxWidth: 500 }}
														placement="topRight"
														content={contentMakeQuestions}
														title={
															makeQuestions
																? t('assistant.assistantMakeQuestionsPopoverTitleTrue')
																: t('assistant.assistantMakeQuestionsPopoverTitleFalse')
														}
													>
														<Space size={6}>
															<SubnodeOutlined className="rotate-180" />
															<Switch
																size="small"
																checked={makeQuestions}
																onChange={() => setMakeQuestions((p) => !p)}
															/>
														</Space>
													</Popover>

													<Popover
														overlayStyle={{ maxWidth: 800 }}
														placement="topRight"
														content={contentISearch}
														title={t('assistant.assistantInternetSearchInstructionTitle')}
													>
														<Space size={6}>
															<QuestionCircleOutlined />
															<Switch
																size="small"
																checked={!!isInternetSearching}
																onChange={handleSwitchInternetSearching}
															/>
														</Space>
													</Popover>

													<Popover
														overlayStyle={{ maxWidth: 320 }}
														placement="topRight"
														content={org?.organization?.isEmployee ? contentEmployee : contentUser}
														title={t('assistant.assistantFilesInstructionTitle')}
													/>

													{/* Attach */}
													<Popover
														overlayStyle={{ maxWidth: 320 }}
														placement="topLeft"
														content={contentQuickFile}
														title={
															quickFile
																? t('assistant.assistantQuickFilePopoverTitle')
																: t('assistant.assistantQuickFilePopoverTitleEmpty')
														}
													>
														<Button
															aria-label="Attach a file"
															type="text"
															className="asst-icon-btn"
															onClick={() => !quickFile && quickFileInputRef.current?.click()}
															icon={
																<img
																	src={paperClipIcon}
																	alt="attach"
																	style={{ width: 18, height: 18 }}
																/>
															}
														/>
													</Popover>
												</div>

												{/* Right actions (Mic + Send) */}
												<div className="asst-actions-right">
													<Tooltip
														title={
															loadings.recording
																? t('assistant.stopRecording')
																: t('assistant.startRecording')
														}
													>
														<Button
															type="text"
															className="asst-icon-btn asst-mic-btn"
															onClick={toggleMic}
															loading={loadings.uploadAudio}
															icon={<AudioOutlined style={{ fontSize: 16 }} />}
														/>
													</Tooltip>

													<Button
														className="asst-send-btn"
														type="primary"
														loading={loadings.sendMessage}
														onClick={prevModerateMessage}
														icon={<SendOutlined />}
													>
														{t('assistant.send')}
													</Button>
												</div>
											</div>

											{/* 3) Meta row */}
											<div className="asst-meta-row">
												<Typography.Text type="secondary" className="asst-counter">
													{myMessage.length}/{maxLengthMessage}
												</Typography.Text>
												<div />
											</div>
										</div>

										{/* Hidden inputs preserved */}
										<input
											ref={quickFileInputRef}
											type="file"
											style={{ display: 'none' }}
											onChange={handleAddQuickFile}
										/>
										<input
											ref={fileInputRef}
											type="file"
											style={{ display: 'none' }}
											onChange={handleFileChange}
										/>
									</div>
								</div>
							</Card>
						</div>
					</div>
				</Layout.Content>
			</Layout>
		</ChatContext.Provider>
	);
};

export default AssistantChat;
